// Núcleo do cadastro de desconto v2: regras, validações, frases e prévia. TypeScript puro.
//
// Porte da classe `Component` de docs/superpowers/specs/cadastro-desconto-v2/mockup-novo-desconto.dc.html
// (validada com 45 cenários), com o contrato do backend da spec. Tudo recebe `Draft` (o que o usuário
// decidiu) e `Ctx` (o que veio da rede) e devolve dado puro; nada de React nem de rede.
//
// i18n: este arquivo NÃO passa pelo plugin de build (é .ts fora de src/models), então todo texto de tela é
// `__t('...')` à mão, dentro de função (nunca em constante de módulo: o idioma troca sem reload).
// Frase inteira com %{variavel}; singular e plural em chaves separadas.
import { __t } from '../../../i18n/t'
import {
  addDays, brDate, brl, clip, clockStr, dayLabel, dayShort, diffDays, dowOf, dowShort, fmtN,
  isValidDay, joinList, localStr, lowerFirst, norm, toInt, todayStr,
} from './format'
import {
  Audience, CityStats, Ctx, DataState, DayWindow, DefaultOpt, DiscountKind, Draft, FieldError, FuelEntry, FuelInfo,
  FuelPick, Hours, LimitMode, ListOrigin, Opt, RangeId, SendMode, StationInfo, StepKey, When,
} from './types'

export * from './types'
export { brl, centsFrom, digits, fmtCents, fmtN, joinList, norm } from './format'

// ─── Constantes (só dado técnico; texto de tela nasce em função) ────────────────

/** Token que o app troca pelo primeiro nome do cliente (push, título e corpo da caixa de entrada). */
export const NAME_TOKEN = '{' + '{nome_do_cliente}' + '}'
export const MAX_CENTS = 500
export const CONFIRM_CENTS = 100
export const WARN_POINTS = 1000
export const PUSH_MAX = 130
export const NAME_MAX = 80
export const QUICK_CENTS = [5, 10, 15, 20, 30]
/** O backend só manda a notificação 5 min depois, e o agendador roda de 3 em 3 min. */
export const SEND_NOW_MINUTES = 10
export const MIN_SEND_MINUTES = 10

/** Combustíveis marcados de largada numa promoção (nomes normalizados). */
export const TOP_FUELS: string[] = ['gasolina comum', 'etanol', 'gasolina aditivada']
export const DIESEL_FUELS: string[] = ['diesel s10', 'diesel s500', 'diesel s10 aditivado']
const FUEL_ORDER: string[] = [...TOP_FUELS, 'diesel s10', 'diesel s500', 'diesel s10 aditivado', 'gasolina premium']

// ─── Rich text: "**negrito**" dentro da frase traduzida ─────────────────────────

export interface RichPart { text: string; bold: boolean }

/** Quebra "a **b** c" em partes. Número ímpar de ** vira texto cru. */
export function parseRich(text: string): RichPart[] {
  const bits = text.split('**')
  if (bits.length % 2 === 0) return [{ text: text.split('**').join(''), bold: false }]
  return bits.map((t, i) => ({ text: t, bold: i % 2 === 1 })).filter(p => p.text !== '')
}
export const stripRich = (text: string) => text.split('**').join('')
/** Junta cláusulas com espaço, menos as que começam com pontuação. */
const joinClauses = (parts: string[]) =>
  parts.filter(Boolean).reduce((acc, p) => (acc ? acc + (/^[,.;:]/.test(p) ? '' : ' ') + p : p), '')

// ─── Etapas e tipos ─────────────────────────────────────────────────────────────

export const stepKeys = (kind: DiscountKind | null): StepKey[] =>
  kind === DiscountKind.special
    ? [StepKey.type, StepKey.values, StepKey.who, StepKey.when, StepKey.notice, StepKey.review]
    : [StepKey.type, StepKey.values, StepKey.review]

/** A vitrine de modelos prontos só aparece na primeira passada pela etapa 1 e nunca com uma lista de clientes já escolhida. */
export const showsRecipes = (locked: boolean, maxStep: number): boolean => !locked && maxStep === 0

export interface StepText { label: string; question: string }
export function stepInfo(key: StepKey): StepText {
  switch (key) {
    case StepKey.type:
      return { label: __t('Tipo'), question: __t('O que você quer criar?') }
    case StepKey.values:
      return { label: __t('Postos e valores'), question: __t('Onde vale e quanto é o desconto?') }
    case StepKey.who:
      return { label: __t('Para quem'), question: __t('Para quem vale a promoção?') }
    case StepKey.when:
      return { label: __t('Quando'), question: __t('Quando vale a promoção?') }
    case StepKey.notice:
      return { label: __t('Notificação'), question: __t('Quer avisar os clientes pelo app?') }
    default:
      return { label: __t('Revisar'), question: __t('Confira antes de salvar') }
  }
}

export interface TypeText { title: string; badge: string; desc: string }
export function typeInfo(kind: DiscountKind): TypeText {
  switch (kind) {
    case DiscountKind.default:
      return {
        title: __t('Preço do app no posto'), badge: __t('Padrão'),
        desc: __t('Desconto fixo para todo cliente que abastecer pelo app.'),
      }
    case DiscountKind.special:
      return {
        title: __t('Promoção'), badge: __t('Especial'),
        desc: __t('Vale por um período ou só para certos clientes.'),
      }
    case DiscountKind.reward:
      return {
        title: __t('Recompensa do Clube de Vantagens'), badge: __t('Recompensa'),
        desc: __t('O cliente ganha ao conquistar um brinde do Clube. Vale uma vez.'),
      }
    default:
      return {
        title: __t('Desconto para campanha de recuperação'), badge: __t('Recuperação'),
        desc: __t('Para os níveis de uma campanha de recuperação.'),
      }
  }
}

export const typeDisabledNote = (kind: DiscountKind, locked: boolean): string => {
  if (locked) return __t('Com uma lista de clientes, só dá para criar promoção.')
  if (kind === DiscountKind.reward) return __t('Disponível só para redes com o Clube de Vantagens ativo.')
  return __t('Disponível só para redes com a Recuperação de clientes ativa.')
}

export const isKindAvailable = (kind: DiscountKind, d: Draft, c: Ctx): boolean => {
  if (isLocked(d) && kind !== DiscountKind.special) return false
  if (kind === DiscountKind.recovery) return c.chain.useCustomerRecovery === true
  if (kind === DiscountKind.reward) return c.chain.useGift === true
  return true
}

export const showsLimit = (k: DiscountKind | null) => k === DiscountKind.default || k === DiscountKind.special
export const showsPoints = (k: DiscountKind | null) => k !== DiscountKind.reward
export const showsEarn = (k: DiscountKind | null) =>
  k === DiscountKind.special || k === DiscountKind.reward || k === DiscountKind.recovery
/** O app avisa sozinho o maior desconto que o cliente consegue pagar (não vale para o cupom da campanha). */
export const autoApplies = (k: DiscountKind | null) => k !== DiscountKind.recovery

/** Pontuar ao usar desconto: o que o usuário marcou ou, sem escolha, a configuração da rede. */
export const earnOn = (d: Draft, c: Ctx) => (d.earnPoints == null ? c.chain.pointsWhenDiscounting !== false : d.earnPoints)

export function freshDraft(): Draft {
  return {
    kind: null, stations: [], fuels: {}, preset: { pick: 'none', cents: null },
    hasLimit: false, limitN: '1', limitMode: LimitMode.every, limitHours: '24',
    earnPoints: null, overlap: false,
    audience: Audience.all, tags: [], groups: [], cities: [], useGroups: false, useCity: false, customerIds: [], listOrigin: null,
    when: When.period, range: null, startDate: '', endDate: '',
    hours: Hours.allday, weekdays: [false, false, false, false, false, false, false], allDay: true, startTime: '', endTime: '', dayWindows: null,
    notify: false, title: '', pushText: '', body: '', sendMode: SendMode.now, sendAt: '',
    name: '', nameEdited: false,
  }
}

// ─── Postos ─────────────────────────────────────────────────────────────────────

export const isLocked = (d: Draft) => d.audience === Audience.list

/** Postos escolhidos que existem na rede. Rede de um posto só: ele já vem marcado. */
export function selIds(d: Draft, c: Ctx): string[] {
  if (c.stations.length === 1) return [c.stations[0].id]
  const known = new Set(c.stations.map(s => s.id))
  return d.stations.filter(id => known.has(id))
}
export const selStations = (d: Draft, c: Ctx): StationInfo[] => {
  const ids = selIds(d, c)
  return c.stations.filter(s => ids.includes(s.id))
}
const stationName = (c: Ctx, id: string): string | undefined => c.stations.find(s => s.id === id)?.name
const fuelName = (c: Ctx, id: string): string | undefined => c.fuels.find(f => f.id === id)?.name

// ─── Combustíveis e opções de desconto ──────────────────────────────────────────

export interface FuelRow extends FuelInfo {
  art: 'na' | 'no'
  on: boolean
  opts: Opt[]
  /** Quantos dos postos escolhidos vendem este combustível. */
  n: number
  total: number
}

export const fuelArticle = (name: string): 'na' | 'no' => (/^(gasolina|arla)/.test(norm(name)) ? 'na' : 'no')
const fuelRank = (name: string) => {
  const i = FUEL_ORDER.indexOf(norm(name))
  return i < 0 ? FUEL_ORDER.length : i
}
const byFuel = (a: FuelInfo, b: FuelInfo) => fuelRank(a.name) - fuelRank(b.name) || a.name.localeCompare(b.name)

export const newOpt = (cents: number | null = null): Opt => ({ cents, payPoints: false, pts: '', okHigh: false })

const presetOn = (pick: FuelPick, fuel: FuelInfo) => pick === 'all' || (Array.isArray(pick) && pick.includes(norm(fuel.name)))

export function fuelState(d: Draft, fuel: FuelInfo): FuelEntry {
  return d.fuels[fuel.id] ?? { on: presetOn(d.preset.pick, fuel), opts: [newOpt(d.preset.cents)] }
}

export function fuelRows(d: Draft, c: Ctx): FuelRow[] {
  const sel = selIds(d, c)
  return c.fuels
    .filter(f => f.stationIds.some(id => sel.includes(id)))
    .sort(byFuel)
    .map(f => {
      const st = fuelState(d, f)
      return { ...f, art: fuelArticle(f.name), on: st.on, opts: st.opts, n: f.stationIds.filter(id => sel.includes(id)).length, total: sel.length }
    })
}
export const activeRows = (d: Draft, c: Ctx) => fuelRows(d, c).filter(r => r.on)
export const allOpts = (d: Draft, c: Ctx) => activeRows(d, c).flatMap(r => r.opts)
export const ptsOf = (d: Draft, o: Opt) => (showsPoints(d.kind) && o.payPoints ? toInt(o.pts) : 0)

/** "Gasta pontos" marcado, mas os pontos ainda não foram digitados: a opção ainda não é grátis nem paga. */
export const pointsPending = (d: Draft, o: Opt): boolean => showsPoints(d.kind) && o.payPoints && !(toInt(o.pts) >= 1)

export function valErr(o: Opt): string {
  if (o.cents == null || o.cents <= 0) return __t('Informe o desconto por litro, maior que R$ 0,00.')
  if (o.cents > MAX_CENTS) return __t('O máximo é R$ 5,00 por litro. Confira o valor.')
  if (o.cents >= CONFIRM_CENTS && !o.okHigh) return __t('Valor alto: marque “O valor está certo” para continuar.')
  return ''
}
export function ptsErr(d: Draft, o: Opt): string {
  if (!showsPoints(d.kind) || !o.payPoints) return ''
  return toInt(o.pts) >= 1 ? '' : __t('Informe quantos pontos o cliente gasta.')
}

export const setFuel = (d: Draft, id: string, entry: FuelEntry): Draft => ({ ...d, fuels: { ...d.fuels, [id]: entry } })
export const toggleFuel = (d: Draft, fuel: FuelInfo): Draft => {
  const st = fuelState(d, fuel)
  return setFuel(d, fuel.id, { ...st, on: !st.on })
}
export const updateOpt = (d: Draft, fuel: FuelInfo, i: number, patch: Partial<Opt>): Draft => {
  const st = fuelState(d, fuel)
  return setFuel(d, fuel.id, { ...st, opts: st.opts.map((o, j) => (j === i ? { ...o, ...patch } : o)) })
}
export const setOptCents = (d: Draft, fuel: FuelInfo, i: number, cents: number | null) => updateOpt(d, fuel, i, { cents, okHigh: false })
/** A segunda opção já nasce "Gasta pontos" (é o par comum: uma grátis e outra com pontos). */
export const addOpt = (d: Draft, fuel: FuelInfo): Draft => {
  const st = fuelState(d, fuel)
  if (st.opts.length >= 3) return d
  const opt = { ...newOpt(null), payPoints: showsPoints(d.kind) && !st.opts[0].payPoints }
  return setFuel(d, fuel.id, { on: true, opts: [...st.opts, opt] })
}
export const removeOpt = (d: Draft, fuel: FuelInfo, i: number): Draft => {
  const st = fuelState(d, fuel)
  if (i <= 0 || st.opts.length < 2) return d
  return setFuel(d, fuel.id, { ...st, opts: st.opts.filter((_, j) => j !== i) })
}
/** Mesmo valor em todos os combustíveis marcados (só a primeira opção de cada um). */
export function applyAll(d: Draft, c: Ctx, cents: number): Draft {
  const fuels = { ...d.fuels }
  activeRows(d, c).forEach(r => {
    const st = fuelState(d, r)
    fuels[r.id] = { ...st, opts: st.opts.map((o, i) => (i === 0 ? { ...o, cents, okHigh: false } : o)) }
  })
  return { ...d, fuels }
}

export function multiOptNote(d: Draft, row: FuelRow): string {
  const allFree = row.opts.length > 1 && row.opts.every(o => !o.payPoints)
  return allFree && showsPoints(d.kind)
    ? __t('Todas as opções são grátis: o app sempre aplica a maior. Para dar escolha, faça uma gastar pontos.')
    : ''
}

// ─── Padrão atual: troca e colisão ──────────────────────────────────────────────

/** Opções do Padrão em vigor num combustível. Dado antigo, sem as opções, vale só o maior desconto e sem pontos. */
const defaultOptsOf = (def: { values: Record<string, number>; opts?: Record<string, DefaultOpt[]> }, fuelId: string): DefaultOpt[] => {
  const all = def.opts?.[fuelId]
  if (all && all.length) return all
  return def.values[fuelId] ? [{ cents: def.values[fuelId], pts: 0 }] : []
}

/** "R$ 0,10 ou R$ 0,40 (100 pontos)": o que o Padrão dá num combustível, do maior desconto para o menor. */
export function defaultOptsText(opts: DefaultOpt[]): string {
  const seen = new Set<string>()
  const parts = [...opts]
    .sort((a, b) => b.cents - a.cents || a.pts - b.pts)
    .filter(o => { const k = `${o.cents}|${o.pts}`; if (seen.has(k)) return false; seen.add(k); return true })
    .map(o => {
      if (!o.pts) return brl(o.cents)
      return o.pts === 1 ? __t('%{valor} (1 ponto)', { valor: brl(o.cents) }) : __t('%{valor} (%{pontos} pontos)', { valor: brl(o.cents), pontos: fmtN(o.pts) })
    })
  return joinList(parts, 'or')
}

export interface Collision {
  fuelId: string
  fuel: string
  code: string
  /** Maior desconto do Padrão neste combustível. */
  cents: number
  /** Todas as opções do Padrão neste combustível (valor e pontos). */
  opts: DefaultOpt[]
  where: string
}

/** Promoção em combustível/posto que o Padrão atual já cobre. */
export function collisions(d: Draft, c: Ctx): Collision[] {
  if (d.kind !== DiscountKind.special) return []
  const sel = selIds(d, c)
  const active = activeRows(d, c)
  const out: Collision[] = []
  c.defaults.forEach(def => {
    const st = def.stationIds.filter(id => sel.includes(id))
    if (!st.length) return
    active.forEach(r => {
      const cents = def.values[r.id]
      if (cents) out.push({ fuelId: r.id, fuel: r.name, code: def.code, cents, opts: defaultOptsOf(def, r.id), where: joinList(st.map(id => stationName(c, id) as string)) })
    })
  })
  return out
}

export interface Replacement {
  code: string
  /** Postos escolhidos onde o Padrão deixa de valer */
  inSel: string
  /** Outros postos do mesmo Padrão (continuam valendo, com código novo) */
  rest: string
  hasRest: boolean
  vals: string
  droppedText: string
  droppedDone: string
  hasDropped: boolean
}

export function replacements(d: Draft, c: Ctx): Replacement[] {
  if (d.kind !== DiscountKind.default) return []
  const sel = selIds(d, c)
  const activeIds = activeRows(d, c).map(r => r.id)
  return c.defaults
    .filter(def => def.stationIds.some(id => sel.includes(id)))
    .map(def => {
      const inSelIds = def.stationIds.filter(id => sel.includes(id))
      const inSel = joinList(inSelIds.map(id => stationName(c, id) as string))
      const restNames = def.stationIds.filter(id => !sel.includes(id)).map(id => stationName(c, id)).filter(Boolean) as string[]
      const priced = Object.keys(def.values).filter(id => def.values[id] && fuelName(c, id))
      // só perde desconto o combustível que o posto trocado vende e o novo Padrão não inclui
      const dropped = c.fuels
        .filter(f => def.values[f.id] && !activeIds.includes(f.id) && f.stationIds.some(id => inSelIds.includes(id)))
        .sort(byFuel)
        .map(f => f.name)
      const one = dropped.length === 1
      const lista = joinList(dropped)
      return {
        code: def.code, inSel, rest: joinList(restNames), hasRest: restNames.length > 0,
        vals: c.fuels.filter(f => priced.includes(f.id)).sort(byFuel).map(f => `${f.name} ${defaultOptsText(defaultOptsOf(def, f.id))}`).join(' · '),
        hasDropped: dropped.length > 0,
        droppedText: !dropped.length ? ''
          : one ? __t('%{lista} fica sem desconto no %{onde}, porque este Padrão não inclui esse combustível.', { lista, onde: inSel })
            : __t('%{lista} ficam sem desconto no %{onde}, porque este Padrão não inclui esses combustíveis.', { lista, onde: inSel }),
        droppedDone: !dropped.length ? ''
          : one ? __t('%{lista} ficou sem desconto no %{onde}.', { lista, onde: inSel })
            : __t('%{lista} ficaram sem desconto no %{onde}.', { lista, onde: inSel }),
      }
    })
}

// ─── Período, dias e horários (sempre horário de Brasília) ──────────────────────

export function presetRange(id: RangeId, now: Date): [string, string] {
  const t = todayStr(now)
  if (id === RangeId.today) return [t, t]
  if (id === RangeId.weekend) {
    // sábado: hoje e amanhã; domingo: o PRÓXIMO fim de semana (o rótulo diz isso)
    const sat = addDays(t, (6 - dowOf(t) + 7) % 7)
    return [sat, addDays(sat, 1)]
  }
  if (id === RangeId.week) return [t, addDays(t, 6)]
  return [t, addDays(t, 29)]
}
export const pickPreset = (d: Draft, id: RangeId, now: Date): Draft => {
  const [startDate, endDate] = presetRange(id, now)
  return { ...d, range: id, startDate, endDate }
}
export function presetLabel(id: RangeId, now: Date): string {
  if (id === RangeId.today) return __t('Hoje')
  if (id === RangeId.weekend) return dowOf(todayStr(now)) === 0 ? __t('Próximo fim de semana') : __t('Este fim de semana')
  if (id === RangeId.week) return __t('Próximos 7 dias')
  return __t('Próximos 30 dias')
}

export function periodText(d: Draft, c: Ctx): string {
  if (!d.startDate || !d.endDate) return ''
  if (d.startDate === d.endDate) {
    return d.startDate === todayStr(c.now)
      ? __t('só hoje, %{dia}', { dia: dayLabel(d.startDate) })
      : __t('só em %{dia}', { dia: dayLabel(d.startDate) })
  }
  return __t('de %{inicio} a %{fim}', { inicio: dayLabel(d.startDate), fim: dayLabel(d.endDate) })
}

/** "aos domingos", "às segundas"... (uma chave por dia: a concordância muda no espanhol). */
export const onDay = (i: number): string => [
  __t('aos domingos'), __t('às segundas'), __t('às terças'), __t('às quartas'), __t('às quintas'), __t('às sextas'), __t('aos sábados'),
][i]
/** "todo domingo", "toda terça"... */
export const everyDay = (i: number): string => [
  __t('todo domingo'), __t('toda segunda'), __t('toda terça'), __t('toda quarta'), __t('toda quinta'), __t('toda sexta'), __t('todo sábado'),
][i]
/** "no domingo", "na segunda"... */
export const atDay = (i: number): string => [
  __t('no domingo'), __t('na segunda'), __t('na terça'), __t('na quarta'), __t('na quinta'), __t('na sexta'), __t('no sábado'),
][i]
/** Nome longo para o leitor de tela dos botões de dia da semana. */
export const dayFull = (i: number): string => [
  __t('domingo'), __t('segunda'), __t('terça'), __t('quarta'), __t('quinta'), __t('sexta'), __t('sábado'),
][i]

/** O desconto tem horário de verdade (uma janela por dia): "todo dia, num horário", dias com horário ou horário por dia. */
export const timeWindow = (d: Draft) =>
  d.when === When.period && (d.hours === Hours.daily || (d.hours === Hours.weekdays && (!d.allDay || !!d.dayWindows)))

/** Cópia de um desconto semanal com horários diferentes em cada dia: a tela mostra esses horários e não deixa editar. */
export const perDayOn = (d: Draft) => d.when === When.period && d.hours === Hours.weekdays && !!d.dayWindows

/** Os campos "Das / Às" aparecem só quando há um horário único para editar. */
export const hasTimeInputs = (d: Draft) => timeWindow(d) && !perDayOn(d)

export const allDayWindow = (): DayWindow => ({ start: '00:00', end: '23:59' })
export const isAllDayWindow = (w: DayWindow) => w.start === '00:00' && w.end === '23:59'
export const isOvernightWindow = (w: DayWindow) => w.end < w.start

/** A janela em que o desconto vale num dia da semana (0 = domingo), ou null se não vale ou se vale sem horário. */
export function windowOn(d: Draft, dow: number): DayWindow | null {
  if (d.when !== When.period) return null
  if (d.hours === Hours.daily) return d.startTime && d.endTime ? { start: d.startTime, end: d.endTime } : null
  if (d.hours !== Hours.weekdays || !d.weekdays[dow]) return null
  if (d.dayWindows) return d.dayWindows[dow]
  if (d.allDay) return allDayWindow()
  return d.startTime && d.endTime ? { start: d.startTime, end: d.endTime } : null
}
const DOWS = [0, 1, 2, 3, 4, 5, 6]
const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0]
const windowsOf = (d: Draft): DayWindow[] => DOWS.map(i => windowOn(d, i)).filter((w): w is DayWindow => w !== null)

export const isOvernight = (d: Draft) => windowsOf(d).some(isOvernightWindow)

/**
 * Quando a janela do último dia passa da meia-noite, o período não pode acabar às 23:59: o resto da noite cairia
 * fora. O fim passa a ser o dia seguinte, na hora em que a janela termina.
 */
export function spillOf(d: Draft): { day: string; time: string } | null {
  if (d.when !== When.period || !isValidDay(d.endDate)) return null
  const w = windowOn(d, dowOf(d.endDate))
  return w && isOvernightWindow(w) ? { day: addDays(d.endDate, 1), time: w.end } : null
}

export function spillText(d: Draft): string {
  const spill = spillOf(d)
  if (!spill) return ''
  return __t('No último dia (%{dia}), o horário passa da meia-noite e o desconto vale até %{hora} de %{proximo}.', { dia: dayLabel(d.endDate), hora: spill.time, proximo: dayLabel(spill.day) })
}

const windowText = (w: DayWindow): string => {
  if (isAllDayWindow(w)) return __t('o dia todo')
  return isOvernightWindow(w) ? __t('das %{de} às %{ate} do dia seguinte', { de: w.start, ate: w.end }) : __t('das %{de} às %{ate}', { de: w.start, ate: w.end })
}

/** Os horários de cada dia, para mostrar na cópia de um desconto semanal com horários diferentes (segunda a domingo). */
export function perDayLines(d: Draft): { dow: number; day: string; text: string }[] {
  const windows = d.dayWindows
  if (!windows) return []
  return MONDAY_FIRST
    .filter(i => d.weekdays[i] && windows[i])
    .map(i => ({ dow: i, day: dayFull(i), text: windowText(windows[i] as DayWindow) }))
}

export function hoursText(d: Draft): string {
  if (d.when !== When.period || d.hours === Hours.allday) return ''
  if (d.hours === Hours.weekdays && d.dayWindows) {
    const parts = MONDAY_FIRST.map(i => { const w = d.weekdays[i] ? d.dayWindows![i] : null; return w ? `${onDay(i)}, ${windowText(w)}` : '' }).filter(Boolean)
    return parts.length ? parts.join('; ') : __t('nos dias marcados')
  }
  const janela = d.startTime && d.endTime ? windowText({ start: d.startTime, end: d.endTime }) : __t('no horário escolhido')
  if (d.hours === Hours.weekdays) {
    const names = d.weekdays.map((on, i) => (on ? onDay(i) : '')).filter(Boolean)
    const dias = names.length ? joinList(names) : __t('nos dias marcados')
    return d.allDay ? __t('%{dias}, o dia todo', { dias }) : __t('%{dias}, %{janela}', { dias, janela })
  }
  return __t('todo dia, %{janela}', { janela })
}

export function weekdaysInRange(d: Draft): boolean {
  if (!d.startDate || !d.endDate || d.endDate < d.startDate) return true
  const days = diffDays(d.startDate, d.endDate) + 1
  if (days >= 7) return true
  for (let i = 0; i < days; i++) if (d.weekdays[dowOf(addDays(d.startDate, i))]) return true
  return false
}

export const whenSentence = (d: Draft, c: Ctx): string => {
  const per = periodText(d, c)
  if (!per) return ''
  const h = hoursText(d)
  const base = h ? __t('Vale %{periodo}, %{horas}, no horário de Brasília.', { periodo: per, horas: h }) : __t('Vale %{periodo}, no horário de Brasília.', { periodo: per })
  const spill = spillText(d)
  return spill ? `${base} ${spill}` : base
}

/** "Usar um horário só para todos os dias": sai do horário por dia e deixa os campos Das/Às em branco para o dono preencher. */
export const singleWindow = (d: Draft): Draft => ({ ...d, dayWindows: null, allDay: false, startTime: '', endTime: '' })

// ─── Limite de uso ──────────────────────────────────────────────────────────────

const vezes = (n: number) => (n === 1 ? __t('%{n} vez', { n }) : __t('%{n} vezes', { n }))
const aCada = (h: number) => (h === 1 ? __t('a cada hora') : __t('a cada %{h} horas', { h }))

export function limitPhrase(d: Draft): string {
  const v = vezes(toInt(d.limitN))
  return d.limitMode === LimitMode.every ? `${v} ${aCada(toInt(d.limitHours))}` : __t('%{vezes} no total', { vezes: v })
}
export function limitHeader(d: Draft): string {
  const n = toInt(d.limitN) || 1
  const h = toInt(d.limitHours) || 24
  const base = n === 1 ? __t('%{n} resgate permitido', { n }) : __t('%{n} resgates permitidos', { n })
  return d.limitMode === LimitMode.every ? `${base} ${aCada(h)}` : base
}
export function limitExplain(d: Draft): string {
  const n = toInt(d.limitN)
  const h = toInt(d.limitHours)
  if (d.limitMode === LimitMode.every) {
    return h === 1 ? __t('Conta os usos da última hora.') : __t('Conta os usos das últimas %{h} horas.', { h: h || '?' })
  }
  return n === 1 ? __t('Depois de 1 uso, o desconto some para o cliente.') : __t('Depois de %{n} usos, o desconto some para o cliente.', { n: n || '?' })
}

// ─── Público e notificação ──────────────────────────────────────────────────────

const namesOf = (list: { id: string; name: string }[], ids: string[]) => list.filter(x => ids.includes(x.id)).map(x => x.name)

/** "8 clientes da lista do Painel 360" ou, sem origem conhecida (relatório), "8 clientes da lista". */
const listCount = (d: Draft) => {
  const n = d.customerIds.length
  if (d.listOrigin === ListOrigin.rfm) return n === 1 ? __t('1 cliente da lista do Painel 360') : __t('%{n} clientes da lista do Painel 360', { n: fmtN(n) })
  return n === 1 ? __t('1 cliente da lista') : __t('%{n} clientes da lista', { n: fmtN(n) })
}

/** Quem a escolha de público alcança, por partes: etiquetas, grupos e cidades (cada uma só conta se for usada). */
function audiencePicks(d: Draft, c: Ctx) {
  const some = d.audience === Audience.some
  return {
    tags: some ? namesOf(c.tags, d.tags) : [],
    groups: some && d.useGroups ? namesOf(c.groups, d.groups) : [],
    cities: (some || d.audience === Audience.all) && d.useCity ? namesOf(c.cities, d.cities) : [],
  }
}

/** Abaixo disto, a cobertura do cadastro de cidade vira aviso ("Só 26% dos clientes têm cidade"). */
export const CITY_COVERAGE_LOW = 0.8

/** Quantos clientes têm cidade no cadastro; vazio enquanto a conta não chegou. */
export function coverageText(stats: CityStats | null | undefined): string {
  if (!stats || !(stats.total > 0)) return ''
  const ratio = stats.withCity / stats.total
  const pct = Math.floor(ratio * 100)
  return ratio < CITY_COVERAGE_LOW
    ? __t('Só %{pct}% dos clientes têm cidade no cadastro. Os outros não recebem.', { pct })
    : __t('%{pct}% dos clientes têm cidade no cadastro. Os outros não recebem.', { pct })
}

/** Clientes das cidades escolhidas (cada cliente mora numa cidade só, então a soma não conta ninguém duas vezes). */
export const cityCustomers = (stats: CityStats | null | undefined, cityIds: string[]): number | null =>
  stats ? cityIds.reduce((sum, id) => sum + (stats.cities[id] ?? 0), 0) : null

/**
 * Para quem vai a notificação: o mesmo público da promoção ((grupos OU etiquetas) E cidades), sem público = a rede toda,
 * lista de clientes = exatamente a lista.
 */
export function recipients(d: Draft, c: Ctx): { text: string; note: string } {
  if (d.audience === Audience.list) {
    return { text: d.customerIds.length === 1 ? __t('o cliente da lista') : __t('os %{n} clientes da lista', { n: fmtN(d.customerIds.length) }), note: '' }
  }
  const { tags, groups, cities } = audiencePicks(d, c)
  const cityNote = cities.length ? __t('Quem não tem cidade no cadastro não recebe a notificação.') : ''
  if (!tags.length && !groups.length && !cities.length) {
    return { text: c.counts.chain != null ? __t('todos os clientes do app (%{n})', { n: fmtN(c.counts.chain) }) : __t('todos os clientes do app'), note: '' }
  }
  const who: string[] = []
  if (tags.length) who.push(tags.length === 1 ? __t('com a etiqueta %{nomes}', { nomes: tags[0] }) : __t('com as etiquetas %{nomes}', { nomes: joinList(tags) }))
  if (groups.length) who.push(groups.length === 1 ? __t('do grupo %{nomes}', { nomes: groups[0] }) : __t('dos grupos %{nomes}', { nomes: joinList(groups) }))
  const clauses = [joinList(who, 'or'), cities.length ? __t('que moram em %{nomes}', { nomes: joinList(cities, 'or') }) : ''].filter(Boolean)
  const exact1 = c.counts.audience === 1 && c.counts.audienceExact !== false
  const noun = c.counts.audience == null ? __t('os clientes') : exact1 ? __t('o cliente') : c.counts.audienceExact === false ? __t('os clientes (até %{n})', { n: fmtN(c.counts.audience) }) : __t('os %{n} clientes', { n: fmtN(c.counts.audience) })
  const ceiling = c.counts.audience != null && c.counts.audienceExact === false
  return { text: joinClauses([noun, ...clauses]), note: cityNote || (ceiling ? __t('O número é um máximo: não deu para cruzar todas as escolhas.') : '') }
}

export const sendAfterEnd = (d: Draft) =>
  d.sendMode === SendMode.later && !!d.sendAt && d.when === When.period && !!d.endDate && d.sendAt.slice(0, 10) > d.endDate

/** Horário do envio imediato (+10 min), de Brasília. */
export const sendNowClock = (now: Date) => clockStr(new Date(now.getTime() + SEND_NOW_MINUTES * 60000))
/** Menor horário aceito no agendamento (valor do input datetime-local). */
export const minSendAt = (now: Date) => localStr(new Date(now.getTime() + (MIN_SEND_MINUTES + 1) * 60000))
export function fmtDateTime(local: string): string {
  if (!local) return '—'
  const [day, time] = local.split('T')
  return __t('%{dia} às %{hora}', { dia: dayLabel(day), hora: time || '00:00' })
}
// ─── Nome sugerido ──────────────────────────────────────────────────────────────

export function suggestedName(d: Draft, c: Ctx): string {
  const on = activeRows(d, c)
  const vals = allOpts(d, c).map(o => o.cents || 0).filter(Boolean)
  const max = vals.length ? Math.max(...vals) : 0
  let base = on.length === 1 ? on[0].name : on.length ? __t('%{n} combustíveis', { n: on.length }) : __t('Desconto')
  if (max) base += ` ${brl(max)}`
  let extra = ''
  if (d.kind === DiscountKind.special) {
    if (d.audience === Audience.some) {
      const n = namesOf(c.tags, d.tags)
      if (n.length) extra = n[0]
    }
    if (d.audience === Audience.list) extra = d.listOrigin === ListOrigin.rfm ? __t('lista do Painel 360') : __t('lista de clientes')
    if (d.when === When.period && d.startDate) {
      const idx = d.weekdays.map((on2, i) => (on2 ? i : -1)).filter(i => i >= 0)
      let lbl = dayShort(d.startDate)
      if (d.range === RangeId.weekend) lbl = __t('fim de semana %{data}', { data: dayShort(d.startDate) })
      else if (d.hours === Hours.weekdays && idx.length === 1) lbl = everyDay(idx[0])
      else if (d.hours === Hours.weekdays && idx.length > 1) lbl = joinList(idx.map(dowShort))
      extra = extra ? `${extra} · ${lbl}` : lbl
    }
  }
  return clip(extra ? `${base} · ${extra}` : base, NAME_MAX)
}
export const nameValue = (d: Draft, c: Ctx) => (d.nameEdited ? d.name : suggestedName(d, c))

// ─── Frase "Como vai funcionar" ─────────────────────────────────────────────────

function whoClause(d: Draft, c: Ctx): string {
  if (d.kind === DiscountKind.default) return __t('Todo cliente do app ganha')
  if (d.kind === DiscountKind.reward) return __t('Quem conquistar o brinde do Clube de Vantagens ganha')
  if (d.kind === DiscountKind.recovery) return __t('Quem receber o cupom da campanha de recuperação ganha')
  if (d.audience === Audience.unset) return __t('Os clientes que você escolher ganham')
  if (d.audience === Audience.list) {
    const n = d.customerIds.length
    if (d.listOrigin === ListOrigin.rfm) {
      return n === 1 ? __t('O **cliente da lista do Painel 360** ganha') : __t('Os **%{n} clientes da lista do Painel 360** ganham', { n: fmtN(n) })
    }
    return n === 1 ? __t('O **cliente da lista** ganha') : __t('Os **%{n} clientes da lista** ganham', { n: fmtN(n) })
  }
  const { tags: tg, groups: gr, cities } = audiencePicks(d, c)
  if (d.audience === Audience.all) {
    return cities.length ? __t('Os clientes que moram em **%{nomes}** ganham', { nomes: joinList(cities, 'or') }) : __t('Todo cliente do app ganha')
  }
  if (!tg.length && !gr.length) return __t('Clientes com as etiquetas que você escolher ganham')
  const parts = [__t('Clientes')]
  if (tg.length) parts.push(tg.length === 1 ? __t('da etiqueta **%{nomes}**', { nomes: joinList(tg, 'or') }) : __t('das etiquetas **%{nomes}**', { nomes: joinList(tg, 'or') }))
  if (tg.length && gr.length) parts.push(__t('ou'))
  if (gr.length) parts.push(gr.length === 1 ? __t('do grupo **%{nomes}**', { nomes: joinList(gr, 'or') }) : __t('dos grupos **%{nomes}**', { nomes: joinList(gr, 'or') }))
  if (cities.length) parts.push(__t('que moram em **%{nomes}**', { nomes: joinList(cities, 'or') }))
  parts.push(__t('ganham'))
  return joinClauses(parts)
}

function valueClause(d: Draft, c: Ctx): string {
  const on = activeRows(d, c)
  const vals = allOpts(d, c).map(o => o.cents || 0).filter(v => v > 0)
  if (!vals.length) return __t('**um desconto por litro**')
  const max = Math.max(...vals)
  const min = Math.min(...vals)
  const valor = brl(max)
  const parts = [min === max ? __t('**%{valor} de desconto por litro**', { valor }) : __t('**até %{valor} de desconto por litro**', { valor })]
  if (on.length === 1) parts.push(on[0].art === 'na' ? __t('na **%{combustivel}**', { combustivel: on[0].name }) : __t('no **%{combustivel}**', { combustivel: on[0].name }))
  else if (on.length <= 3) parts.push(__t('em **%{lista}**', { lista: joinList(on.map(r => r.name)) }))
  else parts.push(__t('em **%{n} combustíveis**', { n: on.length }))
  return joinClauses(parts)
}

function whereClause(d: Draft, c: Ctx): string {
  const st = selStations(d, c)
  if (!st.length) return __t('nos postos que você escolher')
  if (st.length === 1) return __t('no **%{posto}**', { posto: st[0].name })
  if (st.length === c.stations.length) return __t(', em **todos os %{n} postos**', { n: st.length })
  return __t(', em **%{n} postos**', { n: st.length })
}

function whenClause(d: Draft, c: Ctx): string {
  if (d.kind === DiscountKind.default) {
    return selStations(d, c).length > 1
      ? __t(', até você criar outro Padrão para esses postos.')
      : __t(', até você criar outro Padrão para o posto.')
  }
  if (d.kind === DiscountKind.special) {
    if (d.when === When.always) return __t(', sem data para acabar.')
    const per = periodText(d, c)
    if (!per) return __t(', no período que você escolher.')
    const h = hoursText(d)
    return h ? __t(', **%{periodo}**, %{horas}.', { periodo: per, horas: h }) : __t(', **%{periodo}**.', { periodo: per })
  }
  return '.'
}

function extraClauses(d: Draft, c: Ctx): string[] {
  const ex: string[] = []
  if (showsLimit(d.kind)) ex.push(d.hasLimit ? __t('Cada cliente pode usar até %{limite}.', { limite: limitPhrase(d) }) : __t('Sem limite de uso.'))
  const paidRows = activeRows(d, c)
    .map(r => ({ name: r.name, pts: r.opts.map(o => ptsOf(d, o)).filter(v => v > 0) }))
    .filter(r => r.pts.length)
  const allPts = paidRows.flatMap(r => r.pts)
  if (allPts.length) {
    ex.push(allPts.every(v => v === allPts[0])
      ? (allPts[0] === 1
        ? __t('Na opção com pontos, o cliente gasta 1 ponto por abastecimento.')
        : __t('Na opção com pontos, o cliente gasta %{pontos} pontos por abastecimento.', { pontos: fmtN(allPts[0]) }))
      : __t('Gasta pontos por abastecimento: %{lista}.', { lista: joinList(paidRows.map(r => `${r.name} ${fmtN(Math.min(...r.pts))}`)) }))
  } else if (d.kind !== DiscountKind.reward && !activeRows(d, c).some(r => r.opts.some(o => pointsPending(d, o)))) ex.push(__t('Não gasta pontos.'))
  if (showsEarn(d.kind) && !earnOn(d, c)) ex.push(__t('Não ganha pontos nesse abastecimento.'))
  return ex
}

/** A frase do resumo, com **negrito** nos valores. Cada cláusula é uma chave de tradução inteira. */
export function sentenceRich(d: Draft, c: Ctx): string {
  if (!d.kind) return __t('Escolha o tipo para ver aqui o resumo do desconto.')
  const main = joinClauses([whoClause(d, c), valueClause(d, c), whereClause(d, c), whenClause(d, c)])
  const extra = extraClauses(d, c)
  return extra.length ? `${main} ${extra.join(' ')}` : main
}
export const sentencePlain = (d: Draft, c: Ctx) => stripRich(sentenceRich(d, c))

// ─── Texto da notificação ───────────────────────────────────────────────────────

export function autoBody(d: Draft, c: Ctx): string {
  const on = activeRows(d, c)
  const vals = allOpts(d, c).map(o => o.cents || 0).filter(Boolean)
  const max = vals.length ? Math.max(...vals) : 0
  const what = max
    ? (vals.every(v => v === max) ? __t('%{valor} de desconto por litro', { valor: brl(max) }) : __t('Até %{valor} de desconto por litro', { valor: brl(max) }))
    : __t('Desconto por litro')
  const fuels = on.length ? __t('em %{lista}', { lista: joinList(on.map(r => r.name)) }) : ''
  const st = selStations(d, c)
  const where = st.length === 1 ? __t('no %{posto}', { posto: st[0].name }) : st.length ? __t('em %{n} postos da rede', { n: st.length }) : ''
  const head = `${[what, fuels, where].filter(Boolean).join(' ')}.`
  const per = d.when === When.always ? '' : periodText(d, c)
  const h = d.when === When.always ? '' : hoursText(d)
  const vale = per ? (h ? __t('Vale %{periodo}, %{horas}.', { periodo: per, horas: h }) : __t('Vale %{periodo}.', { periodo: per })) : ''
  return [head, vale, __t('Abasteça pelo app para usar.')].filter(Boolean).join(' ')
}

/** Título e texto do push sugeridos. Sem o nome do cliente (o botão adiciona), e nunca cortado no meio da frase. */
export function suggestPush(d: Draft, c: Ctx): { title: string; pushText: string } {
  const on = activeRows(d, c)
  const vals = allOpts(d, c).map(o => o.cents || 0).filter(Boolean)
  const max = vals.length ? Math.max(...vals) : 0
  const same = vals.length > 0 && vals.every(v => v === max)
  let title = __t('Promoção no app')
  if (max) {
    if (on.length === 1) title = on[0].art === 'na' ? __t('%{valor} de desconto na %{combustivel}', { valor: brl(max), combustivel: on[0].name }) : __t('%{valor} de desconto no %{combustivel}', { valor: brl(max), combustivel: on[0].name })
    else title = same ? __t('%{valor} de desconto por litro', { valor: brl(max) }) : __t('Até %{valor} de desconto por litro', { valor: brl(max) })
  }
  const per = periodText(d, c)
  const h = hoursText(d)
  const sunday = dowOf(todayStr(c.now)) === 0
  const base = d.when === When.always ? __t('Tem desconto para você')
    : d.range === RangeId.weekend ? (sunday ? __t('É só no próximo fim de semana') : __t('É só neste fim de semana'))
      : d.range === RangeId.today ? __t('É só hoje')
        : per ? __t('Vale %{periodo}', { periodo: per }) : __t('Tem promoção para você')
  const withHours = (b: string) => (h ? __t('%{base}, %{horas}', { base: b, horas: h }) : b)
  const st = selStations(d, c)
  const go = __t('Abasteça pelo app.')
  const goMore = __t('Abasteça pelo app e aproveite.')
  const attempts = (when: string) => {
    const at = st.length === 1 ? __t('%{quando} no %{posto}', { quando: when, posto: st[0].name }) : when
    return [`${at}. ${goMore}`, `${at}. ${go}`, `${when}. ${go}`, `${when}.`]
  }
  const options = [...attempts(withHours(base)), `${base}. ${go}`, `${base}.`]
  const fit = options.find(x => x.length <= PUSH_MAX)
  return { title, pushText: fit ?? clip(`${base}.`, PUSH_MAX) }
}

// ─── Validação por etapa ────────────────────────────────────────────────────────

export function errorsFor(step: StepKey, d: Draft, c: Ctx): FieldError[] {
  const e: FieldError[] = []
  const add = (id: string, msg: string) => e.push({ id, msg })

  if (step === StepKey.type && !d.kind) add('f-type', __t('Escolha o que você quer criar.'))

  if (step === StepKey.values) {
    if (!selIds(d, c).length) add('f-stations', __t('Escolha pelo menos um posto.'))
    else {
      const on = activeRows(d, c)
      if (!on.length) add('f-fuels', __t('Marque pelo menos um combustível.'))
      on.forEach(r => r.opts.forEach((o, i) => {
        const ve = valErr(o)
        if (ve) add(`f-val-${r.id}-${i}`, `${r.name}: ${ve}`)
        const pe = ptsErr(d, o)
        if (pe) add(`f-pts-${r.id}-${i}`, `${r.name}: ${pe}`)
      }))
    }
    if (showsLimit(d.kind) && d.hasLimit) {
      if (!(toInt(d.limitN) >= 1)) add('f-lim-n', __t('Informe quantas vezes cada cliente pode usar (a partir de 1).'))
      if (d.limitMode === LimitMode.every && !(toInt(d.limitHours) >= 1)) add('f-lim-h', __t('Informe o intervalo em horas (a partir de 1).'))
    }
    // Sem saber o Padrão em vigor, um Padrão novo não diz o que vai substituir: espera (ou pede para tentar de novo).
    if (d.kind === DiscountKind.default && c.defaultsState && c.defaultsState !== DataState.ready) {
      add('f-defaults', c.defaultsState === DataState.loading
        ? __t('Aguarde: ainda estamos conferindo o Padrão atual dos postos.')
        : __t('Não foi possível conferir o Padrão atual dos postos. Toque em “Tentar de novo” para continuar.'))
    }
  }

  if (step === StepKey.who) {
    if (d.audience === Audience.unset) add('f-aud', __t('Escolha para quem a promoção vale.'))
    if (d.audience === Audience.some) {
      if (!d.tags.length && !(d.useGroups && d.groups.length)) {
        add('f-tags', d.useGroups ? __t('Escolha pelo menos uma etiqueta ou um grupo.') : __t('Escolha pelo menos uma etiqueta.'))
      }
    }
    if ((d.audience === Audience.some || d.audience === Audience.all) && d.useCity && !d.cities.length) {
      add('f-cities', __t('Escolha pelo menos uma cidade ou desmarque “Limitar pela cidade do cadastro”.'))
    }
  }

  if (step === StepKey.when) {
    if (d.when === When.always && d.audience === Audience.all && !d.useCity) add('f-when', __t('Para todos e sem data para acabar, crie um Padrão ou escolha um período.'))
    if (d.when === When.period) {
      const today = todayStr(c.now)
      if (!isValidDay(d.startDate) || !isValidDay(d.endDate)) add('f-dates', __t('Escolha o período: use um atalho ou preencha as duas datas.'))
      else if (d.startDate < today) add('f-dates', __t('A data de início já passou. Comece hoje ou depois.'))
      else if (d.endDate < d.startDate) add('f-dates', __t('A data de fim precisa ser igual ou depois da data de início.'))
      else if (diffDays(d.startDate, d.endDate) > 366) add('f-dates', __t('O período passa de 1 ano. Confira as datas.'))
      if (d.hours === Hours.weekdays) {
        if (!d.weekdays.some(Boolean)) add('f-weekdays', __t('Marque pelo menos um dia da semana.'))
        else if (!weekdaysInRange(d)) add('f-weekdays', __t('Nenhum dos dias marcados cai dentro do período. Mude os dias ou as datas.'))
      }
      if (hasTimeInputs(d)) {
        if (!d.startTime || !d.endTime) add('f-times', __t('Informe o horário de início e de fim.'))
        else if (d.startTime === d.endTime) add('f-times', __t('O horário de início e o de fim não podem ser iguais.'))
      }
    }
  }

  if (step === StepKey.notice && d.notify) {
    if (!d.title.trim()) add('f-title', __t('Escreva o título da notificação.'))
    if (!d.pushText.trim()) add('f-push', __t('Escreva o texto da notificação.'))
    else if (d.pushText.length > PUSH_MAX) add('f-push', __t('O texto tem %{n} caracteres. O máximo é 130.', { n: d.pushText.length }))
    if (d.sendMode === SendMode.later) {
      if (!d.sendAt) add('f-sendat', __t('Escolha o dia e o horário do envio.'))
      else if (brDate(d.sendAt).getTime() < c.now.getTime() + MIN_SEND_MINUTES * 60000) add('f-sendat', __t('Agende para daqui a pelo menos 10 minutos.'))
    }
  }

  if (step === StepKey.review && d.kind === DiscountKind.special && !nameValue(d, c).trim()) {
    add('f-name', __t('Dê um nome para achar este desconto depois na lista.'))
  }
  return e
}

/** Primeira etapa com problema (para o "Salvar" pular até ela), ou null. */
export function firstInvalidStep(d: Draft, c: Ctx): { index: number; step: StepKey } | null {
  const keys = stepKeys(d.kind)
  for (let i = 0; i < keys.length; i++) if (errorsFor(keys[i], d, c).length) return { index: i, step: keys[i] }
  return null
}

// ─── Mutações das etapas ────────────────────────────────────────────────────────

export function pickType(d: Draft, kind: DiscountKind): Draft {
  if (kind === d.kind) return d
  const untouched = !Object.keys(d.fuels).length && d.preset.cents == null
  const pick: FuelPick = kind === DiscountKind.default ? 'all' : kind === DiscountKind.special ? TOP_FUELS : 'none'
  return { ...d, kind, preset: untouched ? { pick, cents: null } : d.preset }
}

export type RecipeId = 'app' | 'weekend' | 'weekday' | 'tag'
export function recipes(): { id: RecipeId; title: string; desc: string; kind: DiscountKind }[] {
  return [
    { id: 'app', title: __t('Preço do app'), desc: __t('R$ 0,10 por litro em tudo'), kind: DiscountKind.default },
    { id: 'weekend', title: __t('Fim de semana'), desc: __t('R$ 0,20 por litro no sábado e no domingo'), kind: DiscountKind.special },
    { id: 'weekday', title: __t('Um dia da semana'), desc: __t('Toda terça, R$ 0,20 por litro no etanol'), kind: DiscountKind.special },
    { id: 'tag', title: __t('Só para uma etiqueta'), desc: __t('R$ 0,15 por litro no diesel'), kind: DiscountKind.special },
  ]
}
/** Um modelo pronto preenche o cadastro (menos os postos, que o usuário já pode ter escolhido). */
export function applyRecipe(id: RecipeId, d: Draft, now: Date): Draft {
  const base: Draft = { ...freshDraft(), stations: d.stations }
  const t = todayStr(now)
  if (id === 'app') return { ...base, kind: DiscountKind.default, preset: { pick: 'all', cents: 10 } }
  if (id === 'weekend') {
    const [startDate, endDate] = presetRange(RangeId.weekend, now)
    return { ...base, kind: DiscountKind.special, preset: { pick: TOP_FUELS, cents: 20 }, range: RangeId.weekend, startDate, endDate }
  }
  if (id === 'weekday') {
    return {
      ...base, kind: DiscountKind.special, preset: { pick: ['etanol'], cents: 20 }, range: RangeId.month, startDate: t, endDate: addDays(t, 29),
      hours: Hours.weekdays, weekdays: [false, false, true, false, false, false, false], allDay: true,
    }
  }
  return { ...base, kind: DiscountKind.special, preset: { pick: DIESEL_FUELS, cents: 15 }, audience: Audience.some, when: When.always }
}

/** "Todos os clientes" sem data para acabar é um Padrão: oferece criar como Padrão. */
export const toDefault = (d: Draft): Draft => (isLocked(d) ? d : { ...d, kind: DiscountKind.default })

export function turnOnNotify(d: Draft, c: Ctx): Draft {
  const next = { ...d, notify: true }
  return !d.title && !d.pushText ? { ...next, ...suggestPush(d, c) } : next
}
export const addNameToTitle = (d: Draft): Draft => (d.title.includes(NAME_TOKEN) ? d : { ...d, title: `${d.title.trim()} ${NAME_TOKEN}`.trim() })
export const addNameToPush = (d: Draft): Draft =>
  d.pushText.includes(NAME_TOKEN) ? d : { ...d, pushText: d.pushText.trim() ? `${NAME_TOKEN}, ${lowerFirst(d.pushText.trim())}` : `${NAME_TOKEN}, ` }

// ─── Revisão ────────────────────────────────────────────────────────────────────

export interface Check { tone: 'ok' | 'warn' | 'info'; text: string }

function similarSpecial(d: Draft, c: Ctx): { code: string; name: string; when: string } | null {
  if (d.kind !== DiscountKind.special) return null
  const sel = selIds(d, c)
  const fuelIds = activeRows(d, c).map(r => r.id)
  const today = todayStr(c.now)
  for (const r of c.recent) {
    const age = c.now.getTime() - Date.parse(r.createdAt)
    if (!(age >= 0 && age < 24 * 3600 * 1000)) continue
    if (!r.stationIds.some(id => sel.includes(id)) || !r.fuelIds.some(id => fuelIds.includes(id))) continue
    // períodos que se tocam (sem data = sempre)
    const rs = r.startDate || '0000-00-00'
    const re = r.endDate || '9999-12-31'
    const ds = d.when === When.period && d.startDate ? d.startDate : '0000-00-00'
    const de = d.when === When.period && d.endDate ? d.endDate : '9999-12-31'
    if (rs > de || ds > re) continue
    const created = new Date(Date.parse(r.createdAt))
    const hora = clockStr(created)
    const when = todayStr(created) === today ? __t('hoje às %{hora}', { hora }) : __t('ontem às %{hora}', { hora })
    return { code: r.code, name: r.name, when }
  }
  return null
}

export function checks(d: Draft, c: Ctx): Check[] {
  const out: Check[] = []
  const add = (tone: Check['tone'], text: string) => out.push({ tone, text })
  const kind = d.kind
  const opts = allOpts(d, c)
  if (opts.some(o => (o.cents ?? 0) >= CONFIRM_CENTS)) add('warn', __t('Tem desconto de R$ 1,00 ou mais por litro.'))
  replacements(d, c).forEach(r => {
    const base = __t('%{codigo} deixa de valer no %{onde}.', { codigo: r.code, onde: r.inSel })
    add('info', r.hasRest ? `${base} ${__t('No %{rest}, ele continua valendo com um novo código.', { rest: r.rest })}` : base)
    if (r.hasDropped) add('warn', r.droppedText)
  })
  const paid = opts.map(o => ptsOf(d, o)).filter(v => v > 0)
  if (paid.length) {
    const min = Math.min(...paid)
    add('info', min === 1 ? __t('Quem não tem pontos não vê a opção com pontos.') : __t('Quem tem menos de %{n} pontos não vê a opção com pontos.', { n: fmtN(min) }))
  }
  if (kind === DiscountKind.special && d.overlap && collisions(d, c).length && paid.length) {
    add('warn', __t('Quem não tiver pontos para a promoção fica sem nenhum desconto nesses combustíveis.'))
  }
  if (kind === DiscountKind.special && (d.audience === Audience.some || d.audience === Audience.all) && d.useCity) {
    add('info', coverageText(c.cityStats) || __t('Quem não tem cidade no cadastro não recebe esta promoção.'))
  }
  if (kind === DiscountKind.special) {
    const spill = spillText(d)
    if (spill) add('info', spill)
  }
  const similar = similarSpecial(d, c)
  if (similar) add('warn', __t('Parecido com “%{nome}” (%{codigo}), criado %{quando}. Confira se não é repetido.', { nome: similar.name, codigo: similar.code, quando: similar.when }))
  const why: string[] = []
  if (paid.length) why.push(__t('não tiver pontos para a opção com pontos'))
  if (showsLimit(kind) && d.hasLimit) why.push(__t('já tiver usado o limite'))
  if (kind === DiscountKind.special && timeWindow(d)) why.push(__t('estiver fora do horário'))
  else if (kind === DiscountKind.special && d.when === When.period && d.hours === Hours.weekdays) why.push(__t('não for um dos dias marcados'))
  if (kind === DiscountKind.special) {
    const { tags, groups, cities } = audiencePicks(d, c)
    // O público é etiqueta OU grupo: quem não tem nenhum dos dois não vê. A cidade é uma trava a mais.
    if (tags.length && groups.length) why.push(__t('não tiver a etiqueta nem estiver num dos grupos'))
    else if (tags.length) why.push(__t('não tiver a etiqueta'))
    else if (groups.length) why.push(__t('não estiver num dos grupos'))
    if (cities.length) why.push(__t('não morar numa das cidades'))
    if (d.audience === Audience.list) why.push(__t('não estiver na lista de clientes'))
  }
  if (why.length) add('info', __t('O cliente pode não ver este desconto se %{motivos}.', { motivos: joinList(why, 'or') }))
  if (kind === DiscountKind.reward) add('info', __t('Depois de salvar, escolha este desconto num brinde do Clube de Vantagens.'))
  if (kind === DiscountKind.recovery) add('info', __t('Depois de salvar, escolha este desconto num nível da campanha de recuperação.'))
  return out
}

/** Cor da etiqueta na revisão: `brand` é escolha do dono (público, posto), `good`/`info`/`warn` dizem o efeito. */
export type ReviewTone = 'neutral' | 'brand' | 'good' | 'info' | 'warn'
export interface ReviewChip { text: string; tone: ReviewTone }
/** Uma linha por opção; `first` é a primeira opção do combustível (as outras aparecem como "ou"). */
export interface ReviewFuelRow { fuel: string; first: boolean; value: string; cost: ReviewChip | null }
export type ReviewBlock =
  | { kind: 'chips'; k: string; chips: ReviewChip[]; note?: string }
  | { kind: 'fuels'; rows: ReviewFuelRow[]; showCost: boolean }
  | { kind: 'push'; title: string; text: string; body: string }
export interface ReviewSection { step: StepKey; title: string; editLabel: string; blocks: ReviewBlock[] }

const chip = (text: string, tone: ReviewTone): ReviewChip => ({ text, tone })
const chips = (k: string, list: ReviewChip[], note?: string): ReviewBlock => ({ kind: 'chips', k, chips: list, ...(note ? { note } : {}) })
const ptsChip = (pts: number) => chip(pts === 1 ? __t('1 ponto') : __t('%{n} pontos', { n: fmtN(pts) }), 'brand')

/** Seções da revisão, em etiquetas e numa tabela de combustíveis (como o cadastro antigo), com "Alterar" para cada etapa. */
export function reviewSections(d: Draft, c: Ctx): ReviewSection[] {
  if (!d.kind) return []
  const out: ReviewSection[] = []
  const sec = (step: StepKey, title: string, blocks: ReviewBlock[]) =>
    out.push({ step, title, blocks, editLabel: __t('Alterar %{secao}', { secao: title.toLowerCase() }) })

  const st = selStations(d, c)
  const showCost = showsPoints(d.kind)
  const values: ReviewBlock[] = [
    chips(__t('Postos'), st.length > 1 && st.length === c.stations.length ? [chip(__t('Todos os %{n} postos', { n: st.length }), 'brand')] : st.map(s => chip(s.name, 'brand'))),
    {
      kind: 'fuels',
      showCost,
      rows: activeRows(d, c).flatMap(r => r.opts.map((o, i) => {
        const pts = ptsOf(d, o)
        return { fuel: r.name, first: i === 0, value: (o.cents ?? 0) > 0 ? brl(o.cents as number) : '—', cost: showCost ? (pts ? ptsChip(pts) : chip(__t('Grátis'), 'good')) : null }
      })),
    },
  ]
  if (showsLimit(d.kind)) values.push(chips(__t('Uso por cliente'), [d.hasLimit ? chip(__t('Até %{limite}', { limite: limitPhrase(d) }), 'info') : chip(__t('Sem limite'), 'good')]))
  if (showsEarn(d.kind)) values.push(chips(__t('Pontuar ao usar desconto'), [earnOn(d, c) ? chip(__t('Sim'), 'good') : chip(__t('Não'), 'neutral')]))
  if (d.kind === DiscountKind.special && collisions(d, c).length) {
    values.push(chips(__t('Padrão atual'), [d.overlap ? chip(__t('Some para quem recebe a promoção'), 'warn') : chip(__t('Continua aparecendo junto'), 'info')]))
  }
  const rep = replacements(d, c)
  if (rep.length) values.push(chips(__t('Substitui'), rep.map(r => chip(__t('%{codigo} no %{onde}', { codigo: r.code, onde: r.inSel }), 'warn'))))
  sec(StepKey.values, __t('Postos e valores'), values)

  if (d.kind !== DiscountKind.special) return out

  const who: ReviewBlock[] = []
  const { tags, groups, cities } = audiencePicks(d, c)
  if (d.audience === Audience.list) who.push(chips(__t('Clientes'), [chip(listCount(d), 'brand')]))
  else if (d.audience === Audience.unset) who.push(chips(__t('Clientes'), [chip('—', 'neutral')]))
  else {
    if (d.audience === Audience.all && !cities.length) who.push(chips(__t('Clientes'), [chip(__t('Todos os clientes do app'), 'brand')]))
    if (tags.length) who.push(chips(__t('Etiquetas'), tags.map(n => chip(n, 'brand'))))
    if (groups.length) who.push(chips(__t('Grupos'), groups.map(n => chip(n, 'brand'))))
    if (cities.length) who.push(chips(__t('Só quem mora em'), cities.map(n => chip(n, 'info'))))
  }
  if (d.audience !== Audience.list && c.counts.audience != null) {
    who.push(chips(__t('Alcance'), [chip(c.counts.audience === 1 ? __t('1 cliente') : c.counts.audienceExact === false ? __t('Até %{n} clientes', { n: fmtN(c.counts.audience) }) : __t('%{n} clientes', { n: fmtN(c.counts.audience) }), 'neutral')]))
  }
  sec(StepKey.who, __t('Para quem'), who)

  const when: ReviewBlock[] = d.when === When.always
    ? [chips(__t('Período'), [chip(__t('Sem data para acabar'), 'info')])]
    : [
      chips(__t('Período'), [chip(periodText(d, c) || '—', 'info')], spillText(d)),
      chips(__t('Dias e horários'), [chip(d.hours === Hours.allday ? __t('O dia todo') : hoursText(d), 'info')]),
    ]
  sec(StepKey.when, __t('Quando'), when)

  const rep2 = (v: string) => String(v || '').split(NAME_TOKEN).join(__t('Maria'))
  sec(StepKey.notice, __t('Notificação'), d.notify ? [
    { kind: 'push', title: rep2(d.title) || '—', text: rep2(d.pushText) || '—', body: rep2(d.body.trim() || autoBody(d, c)) },
    chips(__t('Envio'), [chip(d.sendMode === SendMode.now ? __t('Logo depois de salvar (até 10 minutos)') : fmtDateTime(d.sendAt), 'info')]),
    chips(__t('Para'), [chip(recipients(d, c).text, 'brand')]),
  ] : [chips(__t('Notificação'), [chip(__t('Não enviar'), 'neutral')])])
  return out
}

export interface Receipt { tone: 'ok' | 'warn'; text: string }

/** O que mudou ao salvar. `splitCodes`: código novo do Padrão que sobrou nos outros postos (se já deu para descobrir). */
export function receipts(d: Draft, c: Ctx, savedAt: Date, splitCodes: Record<string, string> = {}): Receipt[] {
  const out: Receipt[] = []
  replacements(d, c).forEach(r => {
    out.push({ tone: 'ok', text: __t('%{codigo} deixou de valer no %{onde}.', { codigo: r.code, onde: r.inSel }) })
    if (r.hasRest) {
      out.push({
        tone: 'ok',
        text: splitCodes[r.code]
          ? __t('No %{rest}, o Padrão anterior continua valendo como %{novo}.', { rest: r.rest, novo: splitCodes[r.code] })
          : __t('No %{rest}, o Padrão anterior continua valendo, com um novo código.', { rest: r.rest }),
      })
    }
    if (r.hasDropped) out.push({ tone: 'warn', text: r.droppedDone })
  })
  if (d.kind === DiscountKind.special && d.notify) {
    out.push({
      tone: 'ok',
      text: d.sendMode === SendMode.now
        ? __t('Notificação “%{titulo}” sai até as %{hora}.', { titulo: d.title, hora: sendNowClock(savedAt) })
        : __t('Notificação “%{titulo}” agendada para %{quando}.', { titulo: d.title, quando: fmtDateTime(d.sendAt) }),
    })
  }
  return out
}

/** Status honesto logo após salvar: agendado, fora do dia/horário ou já valendo. */
export function savedStatus(d: Draft, c: Ctx): string {
  if (d.kind === DiscountKind.reward) return __t('Próximo passo: em Clube de Vantagens, escolha este desconto num brinde.')
  if (d.kind === DiscountKind.recovery) return __t('Próximo passo: em Recuperação de clientes, escolha este código num nível da campanha.')
  if (d.kind === DiscountKind.special && d.when === When.period) {
    const today = todayStr(c.now)
    if (d.startDate > today) {
      return __t('Começa %{quando}, às 00:00. Até lá, fica em “Agendados” na lista.', { quando: `${atDay(dowOf(d.startDate))}, ${dayShort(d.startDate)}` })
    }
    if (d.hours === Hours.weekdays && !d.weekdays[dowOf(today)]) {
      for (let i = 1; i <= 7; i++) {
        const day = addDays(today, i)
        if (d.weekdays[dowOf(day)] && day <= d.endDate) {
          return __t('Salvo. Vale %{horas}. A próxima vez é %{quando}.', { horas: hoursText(d), quando: `${atDay(dowOf(day))}, ${dayShort(day)}` })
        }
      }
      return __t('Salvo. Vale %{horas}.', { horas: hoursText(d) })
    }
    const todayWindow = windowOn(d, dowOf(today))
    if (todayWindow && !isAllDayWindow(todayWindow)) {
      const hm = clockStr(c.now)
      const inside = todayWindow.start <= todayWindow.end ? hm >= todayWindow.start && hm <= todayWindow.end : hm >= todayWindow.start || hm <= todayWindow.end
      if (!inside) return __t('Salvo. Vale %{horas}; agora está fora desse horário.', { horas: hoursText(d) })
    }
  }
  return __t('Já está valendo no app.')
}

export function savedTitle(d: Draft, c: Ctx): string {
  if (d.kind === DiscountKind.default) {
    const st = selStations(d, c)
    return st.length === 1 ? __t('Padrão do %{posto}', { posto: st[0].name }) : __t('Padrão de %{n} postos', { n: st.length })
  }
  return `“${nameValue(d, c)}”`
}
export const savedNote = (d: Draft): string =>
  d.kind === DiscountKind.special && d.notify
    ? __t('Para mudar alguma coisa depois, duplique este desconto e desative o antigo. Se a notificação ainda não tiver saído, desativar cancela a notificação também.')
    : __t('Para mudar alguma coisa depois, duplique este desconto e desative o antigo na lista.')

export function failInfo(hasReplacements: boolean) {
  return hasReplacements
    ? { title: __t('Não foi possível terminar de salvar.'), text: __t('Seus dados continuam aqui. Antes de tentar de novo, vamos conferir os Padrões dos postos escolhidos.'), button: __t('Conferir e tentar de novo') }
    : { title: __t('Não foi possível salvar agora.'), text: __t('Seus dados continuam aqui. Nenhum desconto foi criado ou desativado.'), button: __t('Tentar de novo') }
}

/** A resposta do servidor se perdeu (rede, tempo esgotado): o desconto pode ter sido criado. */
export function ambiguousFailInfo() {
  return {
    title: __t('Não sabemos se o desconto foi salvo.'),
    text: __t('A resposta do servidor não chegou. Seus dados continuam aqui. Antes de criar de novo, vamos conferir se ele já foi criado.'),
    button: __t('Conferir e tentar de novo'),
  }
}

/** Nem a conferência de "já foi criado?" funcionou. */
export function checkFailInfo() {
  return {
    title: __t('Não deu para conferir se o desconto foi salvo.'),
    text: __t('Seus dados continuam aqui. Veja na lista de descontos se ele já aparece antes de tentar de novo.'),
    button: __t('Conferir e tentar de novo'),
  }
}

/** "Duplicar este desconto": copia tudo; Especial limpa as datas, desliga o aviso e põe "(cópia)" no nome. */
export function duplicateDraft(d: Draft, c: Ctx): { draft: Draft; note: string } {
  const special = d.kind === DiscountKind.special
  const name = d.kind === DiscountKind.default ? savedTitle(d, c) : nameValue(d, c)
  const draft: Draft = {
    ...d,
    name: clip(`${name} ${__t('(cópia)')}`, NAME_MAX), nameEdited: d.kind !== DiscountKind.default,
    ...(special ? { range: null, startDate: '', endDate: '' } : {}),
    notify: false,
  }
  return {
    draft,
    note: special
      ? __t('Cópia de “%{nome}”. Escolha as novas datas em “Quando”. A notificação não é copiada.', { nome: name })
      : __t('Cópia de %{nome}. Confira os postos e os valores antes de salvar.', { nome: name }),
  }
}
